import type { ReactNode } from 'react'
import { styled } from '@linaria/react'
import { MinimalSkipLink } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'

/**
 * Public (pre-auth) device frame — the same centered phone column as the
 * authenticated `MobileShell`, minus the bottom dock. Used by the landing and
 * auth screens so the marketing surface and the app share one silhouette.
 */
const Style = {
  Stage: styled.div`
    min-height: 100dvh;
    display: flex;
    justify-content: center;
    background: ${minimalVars.color.bgApp};

    /* 560px matches no named stop. The phone frame turns to a card here. */
    @media (min-width: 560px) {
      background: ${minimalVars.color.bgSurfaceAlt};
      padding: ${minimalVars.space.md} 0;
      align-items: center;
    }
  `,
  Frame: styled.div`
    position: relative;
    width: 100%;
    max-width: 460px;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${minimalVars.color.bgApp};

    /* 560px matches no named stop. Keeps the frame from filling the viewport. */
    @media (min-width: 560px) {
      height: min(960px, calc(100dvh - ${minimalVars.space.lg}));
      border-radius: 20px;
      border: 1px solid ${minimalVars.color.borderSubtle};
      box-shadow: ${minimalVars.shadow.floating};
    }
  `,
  Scroll: styled.main`
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
  `,
}

export const PublicFrame = ({ children }: { children: ReactNode }) => (
  <Style.Stage>
    <MinimalSkipLink />
    <Style.Frame>
      <Style.Scroll id="main-content" tabIndex={-1}>
        {children}
      </Style.Scroll>
    </Style.Frame>
  </Style.Stage>
)

export const PublicBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${minimalVars.space.sm};
  padding: ${() => `${minimalVars.space.sm} ${minimalVars.space.sm} ${minimalVars.space.lg}`};
  width: 100%;
  box-sizing: border-box;
`
